/**
 * Date formatting utilities for Australian Eastern Time (AEDT/AEST)
 * Automatically handles daylight saving time transitions
 */

/**
 * Format a date string to Australian Eastern Time
 * @param {string} dateString - ISO date string (UTC)
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in AEDT/AEST
 */
export const formatToAEDT = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  
  // Default options for date/time display
  const defaultOptions = {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: options.second !== undefined ? options.second : '2-digit',
    hour12: true,
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  return date.toLocaleString('en-AU', formatOptions);
};

/**
 * Format a date string to date only (no time) in AEDT/AEST
 * @param {string} dateString - ISO date string (UTC)
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  return formatToAEDT(dateString, {
    hour: undefined,
    minute: undefined,
    second: undefined,
    hour12: undefined,
  });
};

/**
 * Format a date string to time only in AEDT/AEST
 * @param {string} dateString - ISO date string (UTC)
 * @returns {string} Formatted time string
 */
export const formatTimeOnly = (dateString) => {
  if (!dateString) return '-';
  return formatToAEDT(dateString, {
    year: undefined,
    month: undefined,
    day: undefined,
  });
};

/**
 * Format a date string with custom format in AEDT/AEST
 * @param {string} dateString - ISO date string (UTC)
 * @param {string} format - Format string (e.g., 'date', 'datetime', 'time')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'datetime') => {
  if (!dateString) return '-';
  
  switch (format) {
    case 'date':
      return formatDateOnly(dateString);
    case 'time':
      return formatTimeOnly(dateString);
    case 'datetime':
    default:
      return formatToAEDT(dateString);
  }
};



